import Header from '../layouts/Header';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Card, Row, Col, Spinner, Alert, Badge, Table, ButtonGroup, Button
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import axiosInstance from '../services/axiosInterceptor';
import { dormitoryMenu } from '../data/Menu';

const quickNavItems = dormitoryMenu.filter(m => m.link !== '/dormitory/home');

const DormitoryHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [genderFilter, setGenderFilter] = useState('all');

  const fetchSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/dormitory/dashboard-summary');
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลแดชบอร์ดหอพักไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const villages = useMemo(() => {
    const rows = summary?.villages || [];
    if (genderFilter === 'all') return rows;
    return rows.filter((v) => v.village_gender === genderFilter);
  }, [summary, genderFilter]);

  const selectedAggregate = useMemo(() => {
    if (!summary) return null;
    if (genderFilter === 'all') return summary.overview;
    if (genderFilter === 'm') return summary.by_gender?.male;
    return summary.by_gender?.female;
  }, [summary, genderFilter]);

  const getProgressColor = (rate) => {
    if (rate >= 100) return 'danger';
    if (rate >= 80) return 'warning';
    if (rate >= 50) return 'info';
    return 'success';
  };

  const genderDonut = useMemo(() => {
    const male = Number(summary?.by_gender?.male?.current_occupancy || 0);
    const female = Number(summary?.by_gender?.female?.current_occupancy || 0);
    return {
      series: [male, female],
      options: {
        labels: ['ชาย', 'หญิง'],
        chart: { type: 'donut', toolbar: { show: false } },
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        colors: ['#0d6efd', '#dc3545']
      }
    };
  }, [summary]);

  const vacancyDonut = useMemo(() => {
    const occupied = Number(selectedAggregate?.current_occupancy || 0);
    const available = Number(selectedAggregate?.available_spaces || 0);
    return {
      series: [available, occupied],
      options: {
        labels: ['ว่าง', 'เต็ม/ถูกใช้งาน'],
        chart: { type: 'donut', toolbar: { show: false } },
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        colors: ['#198754', '#fd7e14']
      }
    };
  }, [selectedAggregate]);

  const exportRows = useMemo(() => {
    return villages.map((v) => ({
      เขตหอพัก: v.village_name,
      เพศ: v.village_gender === 'm' ? 'ชาย' : 'หญิง',
      จำนวนอาคาร: v.building_count,
      ความจุรวม: v.total_capacity,
      เข้าพักแล้ว: v.current_occupancy,
      คงเหลือ: v.available_spaces,
      อัตราเข้าพักเปอร์เซ็นต์: v.occupancy_rate
    }));
  }, [villages]);

  const handleExportExcel = () => {
    if (!summary) return;

    const scopeText = genderFilter === 'all' ? 'ทั้งหมด' : genderFilter === 'm' ? 'ชาย' : 'หญิง';
    const wb = XLSX.utils.book_new();

    const overviewSheet = XLSX.utils.json_to_sheet([
      {
        มุมมอง: scopeText,
        จำนวนเขตหอพัก: summary.overview?.total_villages || 0,
        จำนวนหอพักทั้งหมด: selectedAggregate?.total_buildings ?? selectedAggregate?.building_count ?? 0,
        ความจุรวม: selectedAggregate?.total_capacity || 0,
        เข้าพักแล้ว: selectedAggregate?.current_occupancy || 0,
        ที่ว่างคงเหลือ: selectedAggregate?.available_spaces || 0,
        อัตราเข้าพักเปอร์เซ็นต์: selectedAggregate?.occupancy_rate || 0
      }
    ]);

    const villageSheet = XLSX.utils.json_to_sheet(exportRows);
    const metaSheet = XLSX.utils.json_to_sheet([
      {
        รายงาน: 'รายงานภาพรวมแดชบอร์ดหอพัก',
        ขอบเขตข้อมูล: scopeText,
        วันเวลาที่ออกรายงาน: new Date().toLocaleString('th-TH')
      }
    ]);

    XLSX.utils.book_append_sheet(wb, metaSheet, 'ข้อมูลรายงาน');
    XLSX.utils.book_append_sheet(wb, overviewSheet, 'สรุปภาพรวม');
    XLSX.utils.book_append_sheet(wb, villageSheet, 'รายเขตหอพัก');
    XLSX.writeFile(wb, `dormitory-dashboard-${Date.now()}.xlsx`);
  };

  const handleExportPdf = async () => {
    if (!summary) return;

    const { default: html2canvas } = await import('html2canvas');
    const scopeText = genderFilter === 'all' ? 'ทั้งหมด' : genderFilter === 'm' ? 'ชาย' : 'หญิง';
    const reportRoot = document.createElement('div');
    reportRoot.style.position = 'fixed';
    reportRoot.style.left = '-99999px';
    reportRoot.style.top = '0';
    reportRoot.style.width = '1120px';
    reportRoot.style.padding = '24px';
    reportRoot.style.background = '#ffffff';
    reportRoot.style.color = '#111111';
    reportRoot.style.fontFamily = 'Tahoma, Arial, sans-serif';

    const overviewRows = [
      ['จำนวนเขตหอพัก', String(summary.overview?.total_villages || 0)],
      ['จำนวนหอพักทั้งหมด', String(selectedAggregate?.total_buildings ?? selectedAggregate?.building_count ?? 0)],
      ['ความจุรวม', String(selectedAggregate?.total_capacity || 0)],
      ['เข้าพักแล้ว', String(selectedAggregate?.current_occupancy || 0)],
      ['ที่ว่างคงเหลือ', String(selectedAggregate?.available_spaces || 0)],
      ['อัตราเข้าพัก (%)', String(selectedAggregate?.occupancy_rate || 0)]
    ];

    const villageTableHtml = villages.map((v) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;">${v.village_name}</td>
        <td style="border:1px solid #ddd;padding:6px;">${v.village_gender === 'm' ? 'ชาย' : 'หญิง'}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.building_count}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.total_capacity}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.current_occupancy}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.available_spaces}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.occupancy_rate}</td>
      </tr>
    `).join('');

    const overviewTableHtml = overviewRows.map((row) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;">${row[0]}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row[1]}</td>
      </tr>
    `).join('');

    reportRoot.innerHTML = `
      <h2 style="margin:0 0 8px 0;">รายงานภาพรวมแดชบอร์ดหอพัก</h2>
      <div style="margin:0 0 4px 0;">ขอบเขตข้อมูล: ${scopeText}</div>
      <div style="margin:0 0 16px 0;">วันเวลาที่ออกรายงาน: ${new Date().toLocaleString('th-TH')}</div>
      <h3 style="margin:12px 0 8px 0;">สรุปภาพรวม</h3>
      <table style="border-collapse:collapse;width:100%;margin-bottom:16px;">
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">รายการ</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ค่า</th>
          </tr>
        </thead>
        <tbody>
          ${overviewTableHtml}
        </tbody>
      </table>
      <h3 style="margin:12px 0 8px 0;">สรุปรายเขตหอพัก</h3>
      <table style="border-collapse:collapse;width:100%;">
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">เขตหอพัก</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">เพศ</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">จำนวนอาคาร</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ความจุรวม</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">เข้าพักแล้ว</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">คงเหลือ</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">อัตราเข้าพัก %</th>
          </tr>
        </thead>
        <tbody>
          ${villageTableHtml || '<tr><td colspan="7" style="border:1px solid #ddd;padding:6px;text-align:center;">ไม่พบข้อมูล</td></tr>'}
        </tbody>
      </table>
    `;

    document.body.appendChild(reportRoot);

    const canvas = await html2canvas(reportRoot, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff'
    });

    document.body.removeChild(reportRoot);

    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    if (imgHeight <= pageHeight - 30) {
      doc.addImage(imgData, 'PNG', 20, 15, imgWidth, imgHeight);
    } else {
      let heightLeft = imgHeight;
      let position = 15;
      doc.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 30);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 15;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 30);
      }
    }

    doc.save(`dormitory-dashboard-${Date.now()}.pdf`);
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0 text-center py-5">
            <Spinner animation="border" role="status" />
            <p className="mt-3 mb-0 text-muted">กำลังโหลดข้อมูล Dashboard หอพัก...</p>
          </Container>
        </main>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h2 className="mb-1">Dashboard หอพัก</h2>
            <p className="text-muted mb-0">ภาพรวมความจุ การเข้าพัก และสถานะหอพักสำหรับผู้บริหาร</p>
          </div>
          <ButtonGroup aria-label="Gender Filter">
            <Button variant={genderFilter === 'all' ? 'primary' : 'outline-primary'} onClick={() => setGenderFilter('all')}>
              ทั้งหมด
            </Button>
            <Button variant={genderFilter === 'm' ? 'primary' : 'outline-primary'} onClick={() => setGenderFilter('m')}>
              👨 ชาย
            </Button>
            <Button variant={genderFilter === 'f' ? 'primary' : 'outline-primary'} onClick={() => setGenderFilter('f')}>
              👩 หญิง
            </Button>
          </ButtonGroup>
          <ButtonGroup aria-label="Export Report">
            <Button variant="outline-success" onClick={handleExportExcel} disabled={!summary}>
              ส่งออก Excel
            </Button>
            <Button variant="outline-danger" onClick={handleExportPdf} disabled={!summary}>
              ส่งออก PDF
            </Button>
          </ButtonGroup>
        </div>

        {/* Quick Navigation */}
        <Row className="g-2 mb-4">
          {quickNavItems.map(item => (
            <Col key={item.link} xs={6} sm={4} md={3} xl={2}>
              <Card
                className="h-100 text-center border-0 shadow-sm"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(item.link)}
              >
                <Card.Body className="py-3 px-2">
                  <i className={`${item.icon} fs-3 text-primary mb-2 d-block`} />
                  <small className="fw-medium">{item.label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {!!summary && !!selectedAggregate && (
          <>
            <Row className="g-3 mb-4">
              <Col md={6} xl={3}>
                <Card className="h-100 border-primary">
                  <Card.Body>
                    <small className="text-muted">จำนวนหอพักทั้งหมด</small>
                    <h3 className="mb-0 mt-1">{selectedAggregate.total_buildings ?? selectedAggregate.building_count ?? 0}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-success">
                  <Card.Body>
                    <small className="text-muted">ความจุรวม</small>
                    <h3 className="mb-0 mt-1">{selectedAggregate.total_capacity || 0}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-info">
                  <Card.Body>
                    <small className="text-muted">เข้าพักแล้ว</small>
                    <h3 className="mb-0 mt-1">{selectedAggregate.current_occupancy || 0}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-warning">
                  <Card.Body>
                    <small className="text-muted">ที่ว่างคงเหลือ</small>
                    <h3 className="mb-0 mt-1">{selectedAggregate.available_spaces || 0}</h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header><strong>กราฟโดนัท: สัดส่วนชาย/หญิง (ผู้พักปัจจุบัน)</strong></Card.Header>
                  <Card.Body>
                    <ReactApexChart
                      options={genderDonut.options}
                      series={genderDonut.series}
                      type="donut"
                      height={300}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header><strong>กราฟโดนัท: สัดส่วนว่าง/เต็ม</strong></Card.Header>
                  <Card.Body>
                    <ReactApexChart
                      options={vacancyDonut.options}
                      series={vacancyDonut.series}
                      type="donut"
                      height={300}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col xl={8}>
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>อัตราการเข้าพักรวม</strong>
                    <Badge bg={getProgressColor(selectedAggregate.occupancy_rate || 0)}>
                      {selectedAggregate.occupancy_rate || 0}%
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="progress" style={{ height: '24px' }}>
                      <div
                        className={`progress-bar bg-${getProgressColor(selectedAggregate.occupancy_rate || 0)}`}
                        style={{ width: `${Math.min(100, selectedAggregate.occupancy_rate || 0)}%` }}
                      >
                        {selectedAggregate.occupancy_rate || 0}%
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      คำนวณจากจำนวนเข้าพักจริงเทียบกับความจุรวมของกลุ่มที่เลือก
                    </small>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={4}>
                <Card className="h-100">
                  <Card.Header><strong>สถานะหอพัก</strong></Card.Header>
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span>ว่าง/รองรับเพิ่มได้</span>
                      <Badge bg="success">{summary.overview?.available_buildings || 0} อาคาร</Badge>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>เต็มแล้ว</span>
                      <Badge bg="danger">{summary.overview?.full_buildings || 0} อาคาร</Badge>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>เปิดใช้งาน</span>
                      <Badge bg="primary">{summary.overview?.active_buildings || 0} อาคาร</Badge>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>ปิดใช้งาน</span>
                      <Badge bg="secondary">{summary.overview?.inactive_buildings || 0} อาคาร</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card>
              <Card.Header>
                <strong>สรุปรายเขตหอพัก</strong>
                <small className="text-muted ms-2">(แสดงผลตามตัวกรองเพศ)</small>
              </Card.Header>
              <Card.Body className="p-0">
                {villages.length === 0 ? (
                  <Alert variant="info" className="m-3 mb-0">ไม่พบข้อมูลเขตหอพักสำหรับตัวกรองนี้</Alert>
                ) : (
                  <Table striped hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>เขตหอพัก</th>
                        <th>เพศ</th>
                        <th>จำนวนอาคาร</th>
                        <th>ความจุรวม</th>
                        <th>เข้าพักแล้ว</th>
                        <th>คงเหลือ</th>
                        <th>อัตราเข้าพัก</th>
                      </tr>
                    </thead>
                    <tbody>
                      {villages.map((v) => (
                        <tr key={v.village_id}>
                          <td>{v.village_name}</td>
                          <td>{v.village_gender === 'm' ? <Badge bg="primary">👨 ชาย</Badge> : <Badge bg="danger">👩 หญิง</Badge>}</td>
                          <td>{v.building_count}</td>
                          <td>{v.total_capacity}</td>
                          <td>{v.current_occupancy}</td>
                          <td>{v.available_spaces}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div style={{ width: 90 }}>
                                <div className="progress" style={{ height: '16px' }}>
                                  <div
                                    className={`progress-bar bg-${getProgressColor(v.occupancy_rate)}`}
                                    style={{ width: `${Math.min(100, v.occupancy_rate)}%` }}
                                  />
                                </div>
                              </div>
                              <small>{v.occupancy_rate}%</small>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryHome;
